/**
 * MePage — /me profile page.
 *
 * Sprint 2 Slice 2 Task 20.
 *
 * Displays the current employee's own profile. Renders a "Preferred name"
 * self-edit field that PATCHes `/api/v1/me/preferred-name` and updates the
 * cache optimistically via `setQueryData(['me'])` (no invalidate-refetch race).
 */

import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getMe, updatePreferredName } from '../../api/me'
import { listTeams } from '../../api/teams'
import { PageHead } from '../../components/primitives/PageHead'
import { roleLabel } from '../../lib/role'
import type { Me, Religion, Role } from '@lms/shared-types'

const ME_QUERY_KEY = ['me'] as const

const RELIGION_LABELS: Record<Religion, string> = {
  islam: 'Islam',
  buddhist: 'Buddhist',
  hindu: 'Hindu',
  christian: 'Christian',
  other: 'Other',
}

/**
 * Years of service derived from the join date (whole years + remaining months).
 * Returns '—' when joinDate is missing or invalid, 'Less than a month' for joins
 * within the current month.
 */
function formatYearsOfService(joinDate: string | undefined): string {
  if (!joinDate) return '—'
  const start = new Date(joinDate)
  if (Number.isNaN(start.getTime())) return '—'
  const now = new Date()
  let years = now.getFullYear() - start.getFullYear()
  let months = now.getMonth() - start.getMonth()
  if (now.getDate() < start.getDate()) months -= 1
  if (months < 0) {
    years -= 1
    months += 12
  }
  if (years < 0) return '—'
  if (years === 0 && months === 0) return 'Less than a month'
  const yearStr = years === 0 ? '' : `${years} ${years === 1 ? 'year' : 'years'}`
  const monthStr = months === 0 ? '' : `${months} ${months === 1 ? 'month' : 'months'}`
  return [yearStr, monthStr].filter(Boolean).join(' ')
}

// ── ProfileRow sub-component ──────────────────────────────────────────────────

function ProfileRow({
  label,
  value,
  mono = false,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}): JSX.Element {
  return (
    <div style={{ marginBottom: 14 }}>
      <span
        style={{
          display: 'block',
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--ink-3)',
          marginBottom: 3,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 14,
          color: 'var(--ink-2)',
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── Inner component ───────────────────────────────────────────────────────────

function MePageInner(): JSX.Element {
  const queryClient = useQueryClient()

  const { data: me, isLoading } = useQuery<Me>({
    queryKey: ME_QUERY_KEY,
    queryFn: getMe,
    retry: false,
  })

  const { data: teams } = useQuery({
    queryKey: ['teams'],
    queryFn: listTeams,
  })
  const teamName = teams?.find((t) => t.id === me?.teamId)?.name ?? me?.teamId ?? '—'

  const [localValue, setLocalValue] = useState<string>('')
  const [initialized, setInitialized] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // Sync local input from remote once on first load
  if (me !== undefined && !initialized) {
    setLocalValue(me.preferredName ?? '')
    setInitialized(true)
  }

  const saveMutation = useMutation<Me, Error, void>({
    mutationFn: () =>
      updatePreferredName({ preferredName: localValue.trim() || null }),
    onSuccess: (updated) => {
      queryClient.setQueryData<Me>(ME_QUERY_KEY, updated)
      setSaveError(null)
      setSaved(true)
    },
    onError: (err) => {
      setSaveError(err.message ?? 'Failed to save preferred name')
      setSaved(false)
    },
  })

  const handleSave = useCallback(() => {
    setSaved(false)
    setSaveError(null)
    saveMutation.mutate()
  }, [saveMutation])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value)
    setSaved(false)
    setSaveError(null)
  }, [])

  if (isLoading) {
    return (
      <>
        <PageHead eyebrow="Personal" title="My Profile" />
        <p
          data-testid="me-loading"
          style={{ fontSize: 14, color: 'var(--ink-2)' }}
        >
          Loading…
        </p>
      </>
    )
  }

  return (
    <>
      <PageHead eyebrow="Personal" title="My Profile" />

      {/* ── Identity card ─────────────────────────────────────────────────────── */}
      <section
        data-testid="me-identity-card"
        style={{
          background: 'var(--paper-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-lg, 12px)',
          padding: '24px',
          marginBottom: 24,
          maxWidth: 560,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <ProfileRow
            label="Full name"
            value={
              <span
                data-testid="me-full-name"
                style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}
              >
                {me?.name ?? '—'}
              </span>
            }
          />
          <ProfileRow
            label="Email"
            value={<span data-testid="me-email">{me?.email ?? '—'}</span>}
            mono
          />
          <ProfileRow
            label="Job title"
            value={<span data-testid="me-job-title">{me?.jobTitle ?? '—'}</span>}
          />
          <ProfileRow label="Team" value={teamName} />
          <ProfileRow
            label="Role"
            value={me?.role ? roleLabel(me.role as Role) : '—'}
          />
          <ProfileRow
            label="Gender"
            value={
              me?.gender === 'M'
                ? 'Male'
                : me?.gender === 'F'
                  ? 'Female'
                  : me?.gender === 'X'
                    ? 'Other / Not specified'
                    : '—'
            }
          />
          <ProfileRow
            label="Employment type"
            value={
              me?.employmentType === 'full_time'
                ? 'Full-time'
                : me?.employmentType === 'contractual'
                  ? 'Contractual'
                  : '—'
            }
          />
          <ProfileRow label="Join date" value={me?.joinDate ?? '—'} />
          <ProfileRow
            label="Years of service"
            value={
              <span data-testid="me-years-of-service">
                {formatYearsOfService(me?.joinDate)}
              </span>
            }
          />
          <ProfileRow label="Birth date" value={me?.birthDate ?? '—'} />
          <ProfileRow
            label="Religion"
            value={
              <span data-testid="me-religion">
                {me?.religion ? RELIGION_LABELS[me.religion] : '—'}
              </span>
            }
          />
          <ProfileRow label="On probation" value={me?.onProbation ? 'Yes' : 'No'} />
          <ProfileRow label="Is manager" value={me?.isManager ? 'Yes' : 'No'} />
          <ProfileRow label="Is HOD" value={me?.isHod ? 'Yes' : 'No'} />
        </div>
      </section>

      {/* ── Preferred name self-edit ────────────────────────────────────────── */}
      <section
        data-testid="me-preferred-name-section"
        style={{
          background: 'var(--paper-raised)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-lg, 12px)',
          padding: '24px',
          maxWidth: 560,
        }}
      >
        <label
          htmlFor="preferred-name-input"
          style={{
            display: 'block',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--ink-3)',
            marginBottom: 4,
          }}
        >
          Preferred name
        </label>
        <p
          style={{
            fontSize: 13,
            color: 'var(--ink-3)',
            margin: '0 0 12px',
          }}
        >
          How you&apos;d like to be addressed. Leave blank to use your full name.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            id="preferred-name-input"
            data-testid="preferred-name-input"
            type="text"
            value={localValue}
            onChange={handleChange}
            placeholder={me?.name ?? ''}
            maxLength={100}
            style={{
              flex: 1,
              height: 40,
              padding: '0 12px',
              fontFamily: 'var(--font-sans)',
              fontSize: 14,
              color: 'var(--ink)',
              background: 'var(--paper-sunken)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md, 8px)',
              outline: 'none',
            }}
          />
          <button
            data-testid="preferred-name-save"
            type="button"
            disabled={saveMutation.isPending}
            onClick={handleSave}
            style={{
              height: 40,
              padding: '0 16px',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--r-md, 8px)',
              fontFamily: 'var(--font-sans)',
              fontSize: 13,
              fontWeight: 500,
              cursor: saveMutation.isPending ? 'not-allowed' : 'pointer',
              opacity: saveMutation.isPending ? 0.6 : 1,
              minWidth: 60,
              minHeight: 44,
            }}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>

        {saved && !saveError && (
          <p
            data-testid="preferred-name-success"
            role="status"
            style={{ marginTop: 8, fontSize: 13, color: 'var(--success)' }}
          >
            Preferred name updated.
          </p>
        )}

        {saveError && (
          <p
            data-testid="preferred-name-error"
            role="alert"
            style={{ marginTop: 8, fontSize: 13, color: 'var(--danger)' }}
          >
            {saveError}
          </p>
        )}
      </section>
    </>
  )
}

// ── Public export ─────────────────────────────────────────────────────────────

export function MePage(): JSX.Element {
  return <MePageInner />
}
